import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  Upload,
  ImageIcon,
  Camera,
  AlertCircle,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEDDING_DATE = new Date("2026-06-05T12:00:00Z");

export default function GalleryPage() {
  const [guest, setGuest] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  // Guest identification
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const isUploadEnabled = Date.now() >= WEDDING_DATE.getTime();

  const loadPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setPhotos(res.data);
    } catch (err) {
      console.error("Errore caricamento galleria:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("weddingGuest");
    if (stored) {
      try {
        setGuest(JSON.parse(stored));
      } catch (err) {
        console.error("Errore parsing guest data:", err);
      }
    }
    loadPhotos();
  }, [loadPhotos]);

  const searchGuests = useCallback(async (q) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/guests/search`, { params: { q } });
      setResults(res.data);
      setShowResults(true);
    } catch (err) {
      console.error("Errore ricerca invitati:", err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchGuests(query), 300);
    return () => clearTimeout(timer);
  }, [query, searchGuests]);

  const selectGuest = (g) => {
    const guestData = { id: g.id, nome: g.nome, cognome: g.cognome };
    setGuest(guestData);
    localStorage.setItem("weddingGuest", JSON.stringify(guestData));
    setShowResults(false);
    setQuery("");
    toast.success(`Benvenuto, ${g.nome}!`);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("guest_id", guest.id);
      formData.append("guest_name", `${guest.nome} ${guest.cognome}`);
      await axios.post(`${API}/gallery/upload`, formData);
      toast.success("Foto caricata!");
      loadPhotos();
    } catch (err) {
      const msg = err.response?.data?.detail || "Errore nel caricamento";
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Not identified
  if (!guest) {
    return (
      <main data-testid="gallery-page" className="min-h-screen pt-20 pb-16 bg-warm-white">
        <div className="max-w-xl mx-auto px-6">
          <div className="text-center py-10">
            <h1 className="font-serif text-4xl sm:text-5xl mb-2" style={{ color: "#2C2C2C" }}>
              Galleria
            </h1>
            <div className="gold-line mb-4" />
            <p className="text-base" style={{ color: "#666666" }}>
              Per accedere alla galleria, cerca il tuo nome
            </p>
          </div>

          <div className="relative" data-testid="gallery-guest-search">
            <div className="relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-sage"
              />
              <Input
                data-testid="gallery-search-input"
                type="text"
                placeholder="Cerca il tuo nome..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => results.length > 0 && setShowResults(true)}
                className="pl-10 h-12 text-base rounded-sm"
                style={{ border: "1px solid rgba(197,165,114,0.4)" }}
              />
              {searching && (
                <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-sage" />
              )}
            </div>
            {showResults && results.length > 0 && (
              <div className="search-results" data-testid="gallery-search-results">
                {results.map((g) => (
                  <button
                    key={g.id}
                    className="w-full text-left px-4 py-3 hover:bg-secondary transition-colors border-b last:border-b-0"
                    onClick={() => selectGuest(g)}
                  >
                    <span className="font-medium" style={{ color: "#2C2C2C" }}>
                      {g.nome} {g.cognome}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="gallery-page" className="min-h-screen pt-20 pb-16 bg-warm-white">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center py-10">
          <h1 className="font-serif text-4xl sm:text-5xl mb-2" style={{ color: "#2C2C2C" }}>
            Galleria
          </h1>
          <div className="gold-line mb-4" />
          <p className="text-sm" style={{ color: "#666666" }}>
            Ciao, {guest.nome}!
          </p>
        </div>

        {/* Upload Section */}
        <div className="text-center mb-10">
          {isUploadEnabled ? (
            <div>
              <Label
                htmlFor="photo-upload"
                className="inline-flex items-center gap-2 cursor-pointer"
              >
                <Button
                  data-testid="upload-photo-button"
                  className="btn-press touch-target rounded-sm"
                  style={{ backgroundColor: "#8B9A6B", color: "white" }}
                  disabled={uploading}
                  asChild
                >
                  <span>
                    {uploading ? (
                      <Loader2 size={18} className="animate-spin mr-2" />
                    ) : (
                      <Camera size={18} className="mr-2" />
                    )}
                    {uploading ? "Caricamento..." : "Carica una foto"}
                  </span>
                </Button>
              </Label>
              <input
                id="photo-upload"
                data-testid="photo-upload-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
              />
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 px-4 py-3 rounded-sm"
              style={{
                backgroundColor: "#F5E6D3",
                border: "1px solid rgba(197,165,114,0.3)",
              }}
              data-testid="upload-disabled-notice"
            >
              <AlertCircle size={18} className="text-gold" />
              <span className="text-sm" style={{ color: "#666666" }}>
                L'upload delle foto sarà disponibile dal giorno del matrimonio
              </span>
            </div>
          )}
        </div>

        {/* Photo Grid */}
        {loading && (
          <div className="text-center py-20">
            <Loader2 size={32} className="animate-spin mx-auto text-sage" />
          </div>
        )}
        {!loading && photos.length === 0 && (
          <div
            data-testid="gallery-empty"
            className="text-center py-20"
          >
            <ImageIcon size={48} className="mx-auto mb-4" style={{ color: "#E5E5E5" }} />
            <p className="text-base" style={{ color: "#666666" }}>
              Nessuna foto ancora. Le foto appariranno qui dopo il matrimonio!
            </p>
          </div>
        )}
        {!loading && photos.length > 0 && (
          <div className="photo-grid" data-testid="photo-grid">
            {photos.map((photo) => (
              <button
                key={photo.id}
                data-testid={`photo-${photo.id}`}
                className="relative aspect-square overflow-hidden rounded-sm cursor-pointer group"
                onClick={() => setLightbox(photo)}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                  alt={`Foto di ${photo.guest_name}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              </button>
            ))}
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
          <DialogContent className="max-w-3xl p-2" data-testid="photo-lightbox">
            <DialogHeader className="sr-only">
              <DialogTitle>Foto</DialogTitle>
            </DialogHeader>
            {lightbox && (
              <div>
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${lightbox.url}`}
                  alt={`Foto di ${lightbox.guest_name}`}
                  className="w-full rounded-sm"
                />
                <p className="text-sm text-center mt-2" style={{ color: "#666666" }}>
                  Caricata da {lightbox.guest_name}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
